import { Footer } from '@/components/Footer'
import { Head } from '@/components/Head'
import { Header } from '@/components/Header'
import { Page } from '@/components/Page'
import { Lang, LANG, PREVIEW_DOCUMENT_ID_COOKIE, SHORT_LANG } from '@/const'
import { createClient } from '@/prismicio'
import { cookie as prismicCookie } from '@prismicio/client'
import clsx from 'clsx'
import cookie from 'js-cookie'
import prismicConfig from '../prismic.config.json'
import { FooterDocument, HeaderDocument, PageDocument } from '../prismicio-types'
import styles from './Main.module.css'

export class PreviewManager {
	private readonly client = createClient()
	public readonly previewMode: boolean

	constructor() {
		this.previewMode = PreviewManager.isPreviewing()
	}

	private static isPreviewing(): boolean {
		const value = cookie.get(prismicCookie.preview)
		if (!value) {
			return false
		}

		try {
			return !!JSON.parse(value)[`${prismicConfig.repositoryName}.prismic.io`]
		} catch {
			return false
		}
	}

	async fetch(route: string): Promise<string> {
		const { pathname } = new URL(route, window.location.origin)
		// routes look like /<shortLang>/<uid>/, see linkResolver
		const [shortLang, uid = 'index'] = pathname.split('/').filter(Boolean)
		const lang = LANG[shortLang as keyof typeof LANG]

		let page: PageDocument

		if (uid === 'unpublished') {
			// set by preview.ts before redirecting here
			const documentId = cookie.get(PREVIEW_DOCUMENT_ID_COOKIE)
			const doc = documentId ? await this.client.getByID(documentId, { lang: '*' }) : undefined

			if (doc?.type !== 'page') {
				// fallback to index if document is not a page, like header or footer
				window.location.href = window.location.origin
				return ''
			}

			page = doc as PageDocument
		} else {
			page = await this.client.getByUID<PageDocument>('page', uid, { lang })
		}

		const [header, footer] = await Promise.all([
			this.client.getSingle<HeaderDocument>('header', { lang: page.lang }).catch(() => undefined),
			this.client.getSingle<FooterDocument>('footer', { lang: page.lang }).catch(() => undefined)
		])

		const {
			alternate_languages,
			data: { bg, pagetitle, fixed }
		} = page

		return /* HTML */ `
			${Head.render({ shortLang: SHORT_LANG[page.lang as Lang], title: pagetitle ?? '' })}
			<body class="${clsx(styles.Body, bg === 'black' && styles.Black, fixed && styles.IsFixed)}">
				<div class="${styles.Wrapper}">
					<div class="${styles.Main}">
						<div class="${styles.Content}">${Page.render(page)} ${footer ? Footer.render(footer) : ''}</div>
					</div>
					${header ? Header.render(header, alternate_languages?.[0]) : ''}
				</div>
				<div class="${styles.Container}"></div>
			</body>
		`
	}
}
